/**
 * Country Configuration — Single Source of Truth
 *
 * When a country is selected (onboarding or settings), the entire app
 * adapts: tax fields, rates, org types, FY start, compliance calendar.
 *
 * Grouped by Wave (simplest first):
 *   Wave 1: India-like GST (IN, SG, AU, NZ, MY)
 *   Wave 2: VAT countries (GCC, UK, Africa, SE Asia)
 *   Wave 3: Multi-component (CA, EU)
 *   Wave 4: USA (state sales tax)
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaxIdField {
    label: string;
    placeholder: string;
    hint?: string;
    maxLength?: number;
    pattern?: RegExp;
    transform?: 'uppercase' | 'none';
}

export interface OrgType {
    value: string;
    label: string;
}

export interface ComplianceFilingTemplate {
    id: string;
    name: string;
    category: 'gst' | 'vat' | 'sales_tax' | 'income_tax' | 'payroll' | 'corporate' | 'other';
    frequencyType: 'monthly' | 'bi_monthly' | 'quarterly' | 'semi_annual' | 'annual';
    /** Day of the month due */
    dueDayOfMonth: number;
    /** Offset in months from the period end (1 = next month, 3 = 3 months after) */
    dueMonthOffset: number;
    description: string;
}

export interface AddressConfig {
    stateLabel: string;
    stateRequired: boolean;
    /**
     * null = open text, string[] = dropdown list
     */
    stateOptions: string[] | null;
    postalLabel: string;
    postalPattern?: string;
}

export interface CountryConfig {
    code: string;          // ISO 3166-1 alpha-2
    name: string;
    flag: string;
    currency: string;
    currencySymbol: string;
    numberFormat: string;  // Intl.NumberFormat locale
    dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
    fyStartMonth: number;  // 1 = January, 4 = April, 7 = July
    taxSystemName: string; // 'GST' | 'VAT' | 'SST' | 'Sales Tax' | 'TVA'
    taxRates: number[];
    defaultTaxRate: number;
    hasZeroRate: boolean;
    primaryTaxId: TaxIdField;
    /** Extra IDs shown below primary (e.g. PAN, TAN for India) */
    additionalTaxIds?: TaxIdField[];
    /** India-only toggles (HSN, RCM, e-Invoice, TDS, TCS) */
    hasIndianCompliance: boolean;
    /** Whether the country charges tax on both intra-state and inter-state (India model) */
    hasDualTax: boolean;
    /** Dual tax labels, e.g. CGST/SGST or N/A */
    dualTaxLabels?: { local: string; central: string; integrated: string };
    orgTypes: OrgType[];
    complianceFilings: ComplianceFilingTemplate[];
    addressConfig: AddressConfig;
    wave: 1 | 2 | 3 | 4;  // rollout wave

    // ── EU VAT (Phase 4) ──
    /** Whether this country is an EU member state (enables reverse charge logic) */
    isEU?: boolean;
    /** Whether EU B2B reverse charge applies for cross-border sales */
    hasReverseCharge?: boolean;
    /** Reduced VAT rates (e.g. [5.5, 10] for France) — for rate-selector dropdowns */
    reducedRates?: number[];
    /** Local name of the tax (e.g. 'MwSt' for Germany, 'TVA' for France, 'IVA' for Italy) */
    taxLocalName?: string;

    // ── Canada (Phase 4) ──
    /** Whether this country has province-level tax variations (Canada) */
    hasProvincialTax?: boolean;

    // ── USA (Phase 5) ──
    /** Whether tax is determined at state level (US model vs national rate) */
    hasStateLevelTax?: boolean;
    /** Whether nexus (economic/physical presence) determines tax obligation */
    hasNexusRequirement?: boolean;
    /** Whether product category affects taxability (food, clothing, SaaS) */
    hasProductExemptions?: boolean;
}

// ─── Wave 1: India-like GST ───────────────────────────────────────────────────

const INDIA_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
    'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
    'Andaman & Nicobar', 'Dadra & Nagar Haveli', 'Lakshadweep',
];

const INDIA_ORG_TYPES: OrgType[] = [
    { value: 'in_pvt_ltd', label: 'Private Limited Company (Pvt. Ltd.)' },
    { value: 'in_public_ltd', label: 'Public Limited Company (Ltd.)' },
    { value: 'in_llp', label: 'Limited Liability Partnership (LLP)' },
    { value: 'in_opc', label: 'One Person Company (OPC)' },
    { value: 'in_sole_prop', label: 'Sole Proprietorship' },
    { value: 'in_partnership', label: 'Partnership Firm' },
    { value: 'in_section8', label: 'Section 8 Company (Non-Profit)' },
    { value: 'in_producer', label: 'Producer Company' },
    { value: 'in_trust', label: 'Trust' },
    { value: 'in_society', label: 'Society' },
    // flow-9 (decision D): a co-operative society, under its state's Act; files ITR-5.
    { value: 'in_coop', label: 'Co-operative Society' },
];

const INDIA_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'gstr1', name: 'GSTR-1 (Outward Supplies)', category: 'gst', frequencyType: 'monthly', dueDayOfMonth: 11, dueMonthOffset: 1, description: 'Monthly outward supplies return' },
    { id: 'gstr3b', name: 'GSTR-3B (Summary Return)', category: 'gst', frequencyType: 'monthly', dueDayOfMonth: 20, dueMonthOffset: 1, description: 'Monthly GST summary return' },
    { id: 'gstr9', name: 'GSTR-9 (Annual Return)', category: 'gst', frequencyType: 'annual', dueDayOfMonth: 31, dueMonthOffset: 9, description: 'Annual GST return' },
    { id: 'tds_q', name: 'TDS Return (Quarterly)', category: 'payroll', frequencyType: 'quarterly', dueDayOfMonth: 31, dueMonthOffset: 1, description: 'Quarterly TDS filing' },
    { id: 'itr', name: 'Income Tax Return', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 31, dueMonthOffset: 4, description: 'Annual income tax filing' },
    { id: 'aoc4', name: 'AOC-4 (Financial Statements)', category: 'corporate', frequencyType: 'annual', dueDayOfMonth: 30, dueMonthOffset: 7, description: 'ROC annual financial filing' },
    { id: 'mgt7', name: 'MGT-7A (Annual Return)', category: 'corporate', frequencyType: 'annual', dueDayOfMonth: 29, dueMonthOffset: 8, description: 'ROC annual return' },
];

const SINGAPORE_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'gst_f5', name: 'GST F5 Return', category: 'gst', frequencyType: 'quarterly', dueDayOfMonth: 31, dueMonthOffset: 1, description: 'Quarterly GST return to IRAS' },
    { id: 'corp_tax', name: 'Corporate Income Tax (Form C-S)', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 30, dueMonthOffset: 11, description: 'Annual corporate tax filing to IRAS' },
    { id: 'estimated_chargeable', name: 'Estimated Chargeable Income (ECI)', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 31, dueMonthOffset: 3, description: 'ECI filing within 3 months of FY end' },
];

const AUSTRALIA_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'bas', name: 'Business Activity Statement (BAS)', category: 'gst', frequencyType: 'quarterly', dueDayOfMonth: 28, dueMonthOffset: 1, description: 'Quarterly BAS to ATO' },
    { id: 'ias', name: 'Instalment Activity Statement (IAS)', category: 'income_tax', frequencyType: 'monthly', dueDayOfMonth: 21, dueMonthOffset: 1, description: 'Monthly PAYG instalment' },
    { id: 'tax_return_au', name: 'Company Tax Return', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 28, dueMonthOffset: 5, description: 'Annual company tax return to ATO (Oct 31 or Feb 28 if via tax agent)' },
];

const NZ_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'gst_nz', name: 'GST Return', category: 'gst', frequencyType: 'bi_monthly', dueDayOfMonth: 28, dueMonthOffset: 1, description: 'GST return to IRD (2-monthly or 6-monthly)' },
    { id: 'ir4', name: 'Company Income Tax Return (IR4)', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 7, dueMonthOffset: 7, description: 'Annual company tax return' },
];

const MALAYSIA_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'sst_02', name: 'SST-02 Return', category: 'sales_tax', frequencyType: 'bi_monthly', dueDayOfMonth: 31, dueMonthOffset: 1, description: 'Sales & Service Tax return (every 2 months)' },
    { id: 'form_c', name: 'Corporate Tax Return (Form C)', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 31, dueMonthOffset: 7, description: 'Annual corporate tax — 7 months from FY end' },
];

// ─── Wave 2: VAT Countries ────────────────────────────────────────────────────

const UAE_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'vat_201', name: 'VAT Return (VAT201)', category: 'vat', frequencyType: 'quarterly', dueDayOfMonth: 28, dueMonthOffset: 1, description: 'Quarterly VAT return to FTA' },
    { id: 'uae_corp_tax', name: 'Corporate Tax Return', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 30, dueMonthOffset: 9, description: 'Annual corporate tax (9% on profit >AED 375k)' },
];

const UK_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'vat_uk', name: 'VAT Return', category: 'vat', frequencyType: 'quarterly', dueDayOfMonth: 7, dueMonthOffset: 2, description: 'Quarterly VAT return to HMRC' },
    { id: 'ct600', name: 'Corporation Tax Return (CT600)', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 31, dueMonthOffset: 12, description: 'Annual corporation tax — 12 months from period end' },
    { id: 'paye', name: 'PAYE/RTI Submission', category: 'payroll', frequencyType: 'monthly', dueDayOfMonth: 19, dueMonthOffset: 1, description: 'Monthly payroll RTI to HMRC' },
];

const CANADA_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'gst_hst', name: 'GST/HST Return', category: 'gst', frequencyType: 'quarterly', dueDayOfMonth: 30, dueMonthOffset: 1, description: 'Quarterly GST/HST return to CRA' },
    { id: 't2', name: 'Corporate Income Tax Return (T2)', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 30, dueMonthOffset: 6, description: 'Annual T2 — 6 months after fiscal year end' },
];

const USA_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'sales_tax_us', name: 'Sales Tax Return', category: 'sales_tax', frequencyType: 'monthly', dueDayOfMonth: 20, dueMonthOffset: 1, description: 'State sales tax return (varies by state)' },
    { id: 'form_1120', name: 'Corporate Tax Return (Form 1120)', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 15, dueMonthOffset: 4, description: 'Federal corporate tax — April 15 (or Oct with extension)' },
];

const SA_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'vat_sa', name: 'VAT Return', category: 'vat', frequencyType: 'monthly', dueDayOfMonth: 31, dueMonthOffset: 1, description: 'Monthly VAT return to ZATCA' },
    { id: 'zatca_einvoice', name: 'ZATCA e-Invoice Compliance', category: 'other', frequencyType: 'monthly', dueDayOfMonth: 30, dueMonthOffset: 1, description: 'e-Invoice reporting to ZATCA portal' },
];

const GCC_QUARTERLY_VAT = (country: string): ComplianceFilingTemplate[] => [
    { id: `vat_${country.toLowerCase()}`, name: 'VAT Return', category: 'vat', frequencyType: 'quarterly', dueDayOfMonth: 28, dueMonthOffset: 1, description: 'Quarterly VAT return' },
];

const SOUTH_AFRICA_FILINGS: ComplianceFilingTemplate[] = [
    { id: 'vat_201_za', name: 'VAT Return (VAT201)', category: 'vat', frequencyType: 'bi_monthly', dueDayOfMonth: 25, dueMonthOffset: 1, description: 'VAT201 return to SARS every 2 months' },
    { id: 'itr14', name: 'Corporate Tax Return (ITR14)', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 31, dueMonthOffset: 12, description: 'Annual corporate income tax to SARS' },
];

const GENERIC_VAT_FILINGS = (country: string): ComplianceFilingTemplate[] => [
    { id: `vat_${country.toLowerCase()}`, name: 'VAT Return', category: 'vat', frequencyType: 'quarterly', dueDayOfMonth: 28, dueMonthOffset: 1, description: 'Quarterly VAT return' },
    { id: `corp_${country.toLowerCase()}`, name: 'Corporate Tax Return', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 30, dueMonthOffset: 6, description: 'Annual corporate tax' },
];

// ─── Org Types per Region ─────────────────────────────────────────────────────

const AU_ORG_TYPES: OrgType[] = [
    { value: 'au_pty_ltd', label: 'Proprietary Limited Company (Pty Ltd)' },
    { value: 'au_public_co', label: 'Public Company (Ltd)' },
    { value: 'au_partnership', label: 'Partnership' },
    { value: 'au_sole_trader', label: 'Sole Trader' },
    { value: 'au_trust', label: 'Trust' },
    { value: 'au_ngo', label: 'Not-for-Profit / NGO' },
];

const SG_ORG_TYPES: OrgType[] = [
    { value: 'sg_pte_ltd', label: 'Private Limited Company (Pte. Ltd.)' },
    { value: 'sg_public_ltd', label: 'Public Limited Company (Ltd.)' },
    { value: 'sg_llp', label: 'Limited Liability Partnership (LLP)' },
    { value: 'sg_sole_prop', label: 'Sole Proprietorship' },
    { value: 'sg_partnership', label: 'General Partnership' },
];

const NZ_ORG_TYPES: OrgType[] = [
    { value: 'nz_ltd', label: 'Limited Company (Ltd)' },
    { value: 'nz_partnership', label: 'Partnership' },
    { value: 'nz_sole_trader', label: 'Sole Trader' },
    { value: 'nz_trust', label: 'Trust' },
];

const MY_ORG_TYPES: OrgType[] = [
    { value: 'my_sdn_bhd', label: 'Sendirian Berhad (Sdn. Bhd.)' },
    { value: 'my_bhd', label: 'Berhad (Bhd.) — Public Listed' },
    { value: 'my_llp', label: 'Limited Liability Partnership (PLT)' },
    { value: 'my_sole_prop', label: 'Sole Proprietorship' },
    { value: 'my_partnership', label: 'Partnership' },
];

const GCC_ORG_TYPES: OrgType[] = [
    { value: 'gcc_llc', label: 'Limited Liability Company (LLC)' },
    { value: 'gcc_sole_est', label: 'Sole Establishment' },
    { value: 'gcc_free_zone', label: 'Free Zone Company / FZCO' },
    { value: 'gcc_branch', label: 'Branch of Foreign Company' },
    { value: 'gcc_civil', label: 'Civil Company (Professional)' },
];

const UK_ORG_TYPES: OrgType[] = [
    { value: 'uk_ltd', label: 'Private Limited Company (Ltd)' },
    { value: 'uk_plc', label: 'Public Limited Company (PLC)' },
    { value: 'uk_llp', label: 'Limited Liability Partnership (LLP)' },
    { value: 'uk_sole_trader', label: 'Sole Trader' },
    { value: 'uk_partnership', label: 'Partnership' },
    { value: 'uk_charity', label: 'Charity / Non-Profit' },
];

const CA_ORG_TYPES: OrgType[] = [
    { value: 'ca_corp', label: 'Corporation (Inc. / Ltd. / Corp.)' },
    { value: 'ca_sole_prop', label: 'Sole Proprietorship' },
    { value: 'ca_partnership', label: 'Partnership' },
    { value: 'ca_not_for_profit', label: 'Not-for-Profit' },
];

const US_ORG_TYPES: OrgType[] = [
    { value: 'us_llc', label: 'Limited Liability Company (LLC)' },
    { value: 'us_c_corp', label: 'C Corporation' },
    { value: 'us_s_corp', label: 'S Corporation' },
    { value: 'us_sole_prop', label: 'Sole Proprietorship' },
    { value: 'us_partnership', label: 'Partnership' },
    { value: 'us_nonprofit', label: 'Non-Profit (501(c)(3))' },
];

const EU_ORG_TYPES: OrgType[] = [
    { value: 'eu_ltd', label: 'Limited Company / GmbH / SARL / SRL / BV' },
    { value: 'eu_plc', label: 'Public Company / AG / SA / NV' },
    { value: 'eu_sole', label: 'Sole Proprietor / Einzelunternehmen' },
    { value: 'eu_partnership', label: 'Partnership / OHG / SNC' },
];

const GENERIC_ORG_TYPES: OrgType[] = [
    { value: 'llc', label: 'Limited Liability Company' },
    { value: 'corp', label: 'Corporation' },
    { value: 'sole_prop', label: 'Sole Proprietorship' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'ngo', label: 'NGO / Non-Profit' },
];

// ─── Org Types grouped by capital/equity structure ────────────────────────────
// Single source of truth for any feature that branches on entity structure
// (e.g. the transaction wizard's capital actions). Every value permitted by the
// orgs.org_type DB constraint belongs to exactly one group, across all countries.

/**
 * Single-owner businesses — capital contributed/withdrawn by the owner.
 *
 * flow-4 (24 Sep 2026): `in_opc` left this group. A One Person Company is a
 * company with share capital: "my own money" credited General Reserve and
 * "took money out" raised, because the chart is the corporate one. It now
 * sits with the companies (shares to the one member, a member's loan); the
 * register and ROC gates key on IN_COMPANY_ORG_TYPES and did not move.
 */
export const SOLE_OWNER_ORG_TYPES: string[] = [
    'in_sole_prop',
    'ae_sole_establishment', 'gcc_sole_est',
    'us_sole_prop',
    'uk_sole_trader',
    'au_sole_trader',
    'sg_sole_prop',
    'nz_sole_trader',
    'my_sole_prop',
    'ca_sole_prop',
    'eu_sole',
    'sole_prop',
];

/** Partnerships / LLPs / member-owned LLCs — partner capital contributions. */
export const PARTNERSHIP_ORG_TYPES: string[] = [
    'in_partnership', 'in_llp',
    'ae_civil_company', 'gcc_civil',
    'us_partnership', 'us_llc',
    'uk_partnership', 'uk_llp',
    'au_partnership',
    'sg_partnership', 'sg_llp',
    'nz_partnership',
    'my_partnership', 'my_llp',
    'ca_partnership',
    'eu_partnership',
    'partnership', 'llc',
];

/** Companies with share capital — issue shares, declare dividends. */
export const COMPANY_ORG_TYPES: string[] = [
    'in_pvt_ltd', 'in_public_ltd', 'in_opc', 'in_producer',
    'ae_mainland_llc', 'ae_free_zone',
    'gcc_llc', 'gcc_free_zone',
    'us_c_corp', 'us_s_corp',
    'uk_ltd', 'uk_plc',
    'au_pty_ltd', 'au_public_co',
    'sg_pte_ltd', 'sg_public_ltd',
    'nz_ltd',
    'my_sdn_bhd', 'my_bhd',
    'ca_corp',
    'eu_ltd', 'eu_plc',
    'corp', 'other_for_profit',
];

/**
 * Entities whose owners' money has no everyday answer in Flow (flow-4): a
 * foreign company's branch has no share capital of its own (head office
 * funds it), and an Australian or New Zealand trust's settlement and
 * beneficiary accounts follow its deed. They were counted as a company and as
 * sole owners before; the accountant records these.
 */
export const OTHER_CAPITAL_ORG_TYPES: string[] = ['gcc_branch', 'au_trust', 'nz_trust'];

/**
 * flow-9 (FLOW_SCREEN_SPEC.md sec. 3.9, decision D): owned by its members. Money in is
 * members' share money and deposits; money out is a dividend or patronage to
 * members; there is no owner's drawings and no shareholder register under the
 * Companies Act.
 */
export const MEMBER_OWNED_ORG_TYPES: string[] = ['in_coop'];

/**
 * Indian companies under the Companies Act 2013 — the entities that maintain
 * statutory registers and travel the ROC Journey (Pvt Ltd, Public Ltd, OPC,
 * Section 8). Single source of truth for org-type gates keyed on "is this an
 * Indian company" (Statutory Registers, /roc). LLPs are a different Act.
 */
export const IN_COMPANY_ORG_TYPES = new Set<string>(['in_pvt_ltd', 'in_public_ltd', 'in_opc', 'in_section8', 'in_producer']);

/** Not-for-profits — donations and grants instead of owner/share capital. */
export const NONPROFIT_ORG_TYPES: string[] = [
    'in_section8', 'in_trust', 'in_society',
    'ae_foundation',
    'us_501c3', 'us_nonprofit',
    'uk_charity',
    'au_ngo',
    'ca_not_for_profit',
    'ngo', 'other_non_profit',
];

// ─── Address Configs ──────────────────────────────────────────────────────────

const INDIA_ADDR: AddressConfig = { stateLabel: 'State', stateRequired: true, stateOptions: INDIA_STATES, postalLabel: 'PIN Code', postalPattern: '^[0-9]{6}$' };
const AU_ADDR: AddressConfig = { stateLabel: 'State / Territory', stateRequired: true, stateOptions: ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'], postalLabel: 'Postcode', postalPattern: '^[0-9]{4}$' };
const CA_ADDR: AddressConfig = { stateLabel: 'Province / Territory', stateRequired: true, stateOptions: ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'], postalLabel: 'Postal Code', postalPattern: '^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]$' };
const US_ADDR: AddressConfig = { stateLabel: 'State', stateRequired: true, stateOptions: ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'], postalLabel: 'ZIP Code', postalPattern: '^[0-9]{5}(-[0-9]{4})?$' };
const UK_ADDR: AddressConfig = { stateLabel: 'County', stateRequired: false, stateOptions: null, postalLabel: 'Postcode' };
const SG_ADDR: AddressConfig = { stateLabel: 'District', stateRequired: false, stateOptions: null, postalLabel: 'Postal Code', postalPattern: '^[0-9]{6}$' };
const AE_ADDR: AddressConfig = { stateLabel: 'Emirate', stateRequired: true, stateOptions: ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'], postalLabel: 'P.O. Box' };
const GENERIC_ADDR: AddressConfig = { stateLabel: 'State / Province', stateRequired: false, stateOptions: null, postalLabel: 'Postal Code' };

// ─── Master Country Map ───────────────────────────────────────────────────────

export const COUNTRY_CONFIGS: Record<string, CountryConfig> = {

    // ══════════════════════════════════════
    // WAVE 1 — India-like GST
    // ══════════════════════════════════════

    IN: {
        code: 'IN', name: 'India', flag: '🇮🇳',
        currency: 'INR', currencySymbol: '₹',
        numberFormat: 'en-IN', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 4,
        taxSystemName: 'GST',
        // 1.5 and 40 since 22 Sep 2025 (Notification 9/2025-CT(Rate), Schedules VI and III);
        // 12 and 28 stay for bricks (14/2025) and documents dated before it.
        taxRates: [0, 0.1, 0.25, 1.5, 3, 5, 12, 18, 28, 40],
        defaultTaxRate: 18,
        hasZeroRate: true,
        primaryTaxId: { label: 'GSTIN', placeholder: '22AAAAA0000A1Z5', hint: '15-character GST Identification Number', maxLength: 15, transform: 'uppercase' },
        additionalTaxIds: [
            { label: 'PAN', placeholder: 'AAAAA0000A', hint: 'Permanent Account Number', maxLength: 10, transform: 'uppercase' },
            { label: 'TAN', placeholder: 'AAAA00000A', hint: 'Tax Deduction Account Number', maxLength: 10, transform: 'uppercase' },
        ],
        hasIndianCompliance: true,
        hasDualTax: true,
        dualTaxLabels: { local: 'CGST', central: 'SGST', integrated: 'IGST' },
        orgTypes: INDIA_ORG_TYPES,
        complianceFilings: INDIA_FILINGS,
        addressConfig: INDIA_ADDR,
        wave: 1,
    },

    SG: {
        code: 'SG', name: 'Singapore', flag: '🇸🇬',
        currency: 'SGD', currencySymbol: 'S$',
        numberFormat: 'en-SG', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'GST',
        taxRates: [0, 9],
        defaultTaxRate: 9,
        hasZeroRate: true,
        primaryTaxId: { label: 'UEN', placeholder: '202312345A', hint: 'Unique Entity Number (9–10 chars)', maxLength: 10, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: SG_ORG_TYPES,
        complianceFilings: SINGAPORE_FILINGS,
        addressConfig: SG_ADDR,
        wave: 1,
    },

    AU: {
        code: 'AU', name: 'Australia', flag: '🇦🇺',
        currency: 'AUD', currencySymbol: 'A$',
        numberFormat: 'en-AU', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 7,
        taxSystemName: 'GST',
        taxRates: [0, 10],
        defaultTaxRate: 10,
        hasZeroRate: true,
        primaryTaxId: { label: 'ABN', placeholder: '51 824 753 556', hint: 'Australian Business Number (11 digits)', maxLength: 14 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: AU_ORG_TYPES,
        complianceFilings: AUSTRALIA_FILINGS,
        addressConfig: AU_ADDR,
        wave: 1,
    },

    NZ: {
        code: 'NZ', name: 'New Zealand', flag: '🇳🇿',
        currency: 'NZD', currencySymbol: 'NZ$',
        numberFormat: 'en-NZ', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 4,
        taxSystemName: 'GST',
        taxRates: [0, 15],
        defaultTaxRate: 15,
        hasZeroRate: true,
        primaryTaxId: { label: 'NZBN', placeholder: '9429000000000', hint: 'New Zealand Business Number (13 digits)', maxLength: 13 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: NZ_ORG_TYPES,
        complianceFilings: NZ_FILINGS,
        addressConfig: { stateLabel: 'Region', stateRequired: false, stateOptions: null, postalLabel: 'Postcode', postalPattern: '^[0-9]{4}$' },
        wave: 1,
    },

    MY: {
        code: 'MY', name: 'Malaysia', flag: '🇲🇾',
        currency: 'MYR', currencySymbol: 'RM',
        numberFormat: 'ms-MY', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'SST',
        taxRates: [0, 6, 8, 10],
        defaultTaxRate: 8,
        hasZeroRate: true,
        primaryTaxId: { label: 'SST Registration No.', placeholder: 'W10-1234-12345678', hint: 'Sales & Service Tax registration number', maxLength: 20, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: MY_ORG_TYPES,
        complianceFilings: MALAYSIA_FILINGS,
        addressConfig: { stateLabel: 'State', stateRequired: true, stateOptions: ['Johor', 'Kedah', 'Kelantan', 'Kuala Lumpur', 'Labuan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Penang', 'Perak', 'Perlis', 'Putrajaya', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu'], postalLabel: 'Postcode', postalPattern: '^[0-9]{5}$' },
        wave: 1,
    },

    // ══════════════════════════════════════
    // WAVE 2 — VAT Countries
    // ══════════════════════════════════════

    AE: {
        code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪',
        currency: 'AED', currencySymbol: 'د.إ',
        numberFormat: 'ar-AE', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 5],
        defaultTaxRate: 5,
        hasZeroRate: true,
        primaryTaxId: { label: 'TRN', placeholder: '100123456789003', hint: 'Tax Registration Number (15 digits)', maxLength: 15 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GCC_ORG_TYPES,
        complianceFilings: UAE_FILINGS,
        addressConfig: AE_ADDR,
        wave: 2,
    },

    SA: {
        code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦',
        currency: 'SAR', currencySymbol: '﷼',
        numberFormat: 'ar-SA', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 15],
        defaultTaxRate: 15,
        hasZeroRate: true,
        primaryTaxId: { label: 'VAT Registration No.', placeholder: '3000000000000001', hint: 'Saudi VAT number (15 digits)', maxLength: 16 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GCC_ORG_TYPES,
        complianceFilings: SA_FILINGS,
        addressConfig: { stateLabel: 'Region', stateRequired: false, stateOptions: null, postalLabel: 'Postal Code' },
        wave: 2,
    },

    BH: {
        code: 'BH', name: 'Bahrain', flag: '🇧🇭',
        currency: 'BHD', currencySymbol: 'BD',
        numberFormat: 'ar-BH', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 10],
        defaultTaxRate: 10,
        hasZeroRate: true,
        primaryTaxId: { label: 'VAT Registration No.', placeholder: '012345678912345', hint: 'Bahrain NBR VAT number', maxLength: 15 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GCC_ORG_TYPES,
        complianceFilings: GCC_QUARTERLY_VAT('BH'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    OM: {
        code: 'OM', name: 'Oman', flag: '🇴🇲',
        currency: 'OMR', currencySymbol: 'ر.ع.',
        numberFormat: 'ar-OM', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 5],
        defaultTaxRate: 5,
        hasZeroRate: true,
        primaryTaxId: { label: 'VAT Registration No.', placeholder: 'OM0000100001', hint: 'Oman Tax Authority VAT number', maxLength: 12, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GCC_ORG_TYPES,
        complianceFilings: GCC_QUARTERLY_VAT('OM'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    QA: {
        code: 'QA', name: 'Qatar', flag: '🇶🇦',
        currency: 'QAR', currencySymbol: 'ر.ق',
        numberFormat: 'ar-QA', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'No VAT',
        taxRates: [0],
        defaultTaxRate: 0,
        hasZeroRate: true,
        primaryTaxId: { label: 'CR Number', placeholder: '123456', hint: 'Commercial Registration Number' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GCC_ORG_TYPES,
        complianceFilings: [{ id: 'corp_qa', name: 'Corporate Tax Return', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 30, dueMonthOffset: 4, description: 'Annual corporate income tax (10% flat)' }],
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    KW: {
        code: 'KW', name: 'Kuwait', flag: '🇰🇼',
        currency: 'KWD', currencySymbol: 'د.ك',
        numberFormat: 'ar-KW', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'No VAT',
        taxRates: [0],
        defaultTaxRate: 0,
        hasZeroRate: true,
        primaryTaxId: { label: 'CR Number', placeholder: '123456', hint: 'Commercial Registration Number' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GCC_ORG_TYPES,
        complianceFilings: [{ id: 'corp_kw', name: 'Corporate Tax Return', category: 'income_tax', frequencyType: 'annual', dueDayOfMonth: 30, dueMonthOffset: 4, description: 'Annual corporate income tax (foreign entities only, 15%)' }],
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    GB: {
        code: 'GB', name: 'United Kingdom', flag: '🇬🇧',
        currency: 'GBP', currencySymbol: '£',
        numberFormat: 'en-GB', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 4,
        taxSystemName: 'VAT',
        taxRates: [0, 5, 20],
        defaultTaxRate: 20,
        hasZeroRate: true,
        primaryTaxId: { label: 'VAT Number', placeholder: 'GB123456789', hint: 'UK VAT Registration Number', maxLength: 12, transform: 'uppercase' },
        additionalTaxIds: [
            { label: 'Company Number', placeholder: '12345678', hint: 'Companies House Registration Number', maxLength: 8 },
        ],
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: UK_ORG_TYPES,
        complianceFilings: UK_FILINGS,
        addressConfig: UK_ADDR,
        wave: 2,
    },

    ZA: {
        code: 'ZA', name: 'South Africa', flag: '🇿🇦',
        currency: 'ZAR', currencySymbol: 'R',
        numberFormat: 'en-ZA', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 3,
        taxSystemName: 'VAT',
        taxRates: [0, 15],
        defaultTaxRate: 15,
        hasZeroRate: true,
        primaryTaxId: { label: 'VAT Registration No.', placeholder: '4012345678', hint: 'SARS VAT Registration Number (10 digits)', maxLength: 10 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: SOUTH_AFRICA_FILINGS,
        addressConfig: { stateLabel: 'Province', stateRequired: false, stateOptions: ['Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'], postalLabel: 'Postal Code' },
        wave: 2,
    },

    KE: {
        code: 'KE', name: 'Kenya', flag: '🇰🇪',
        currency: 'KES', currencySymbol: 'KSh',
        numberFormat: 'en-KE', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 7,
        taxSystemName: 'VAT',
        taxRates: [0, 8, 16],
        defaultTaxRate: 16,
        hasZeroRate: true,
        primaryTaxId: { label: 'KRA PIN', placeholder: 'A012345678Z', hint: 'KRA Personal/Tax Identification Number', maxLength: 11, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('KE'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    NG: {
        code: 'NG', name: 'Nigeria', flag: '🇳🇬',
        currency: 'NGN', currencySymbol: '₦',
        numberFormat: 'en-NG', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 7.5],
        defaultTaxRate: 7.5,
        hasZeroRate: true,
        primaryTaxId: { label: 'TIN', placeholder: '12345678-0001', hint: 'FIRS Tax Identification Number', maxLength: 14 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('NG'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    GH: {
        code: 'GH', name: 'Ghana', flag: '🇬🇭',
        currency: 'GHS', currencySymbol: 'GH₵',
        numberFormat: 'en-GH', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 15],
        defaultTaxRate: 15,
        hasZeroRate: true,
        primaryTaxId: { label: 'TIN', placeholder: 'C0000000000', hint: 'GRA Tax Identification Number', maxLength: 11, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('GH'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    PH: {
        code: 'PH', name: 'Philippines', flag: '🇵🇭',
        currency: 'PHP', currencySymbol: '₱',
        numberFormat: 'en-PH', dateFormat: 'MM/DD/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 12],
        defaultTaxRate: 12,
        hasZeroRate: true,
        primaryTaxId: { label: 'TIN', placeholder: '123-456-789-000', hint: 'BIR Tax Identification Number', maxLength: 15 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('PH'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    BD: {
        code: 'BD', name: 'Bangladesh', flag: '🇧🇩',
        currency: 'BDT', currencySymbol: '৳',
        numberFormat: 'bn-BD', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 7,
        taxSystemName: 'VAT',
        taxRates: [0, 5, 7.5, 10, 15],
        defaultTaxRate: 15,
        hasZeroRate: true,
        primaryTaxId: { label: 'BIN', placeholder: '000011111111111', hint: 'Business Identification Number (NBR)', maxLength: 15 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('BD'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    LK: {
        code: 'LK', name: 'Sri Lanka', flag: '🇱🇰',
        currency: 'LKR', currencySymbol: 'Rs',
        numberFormat: 'si-LK', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 18],
        defaultTaxRate: 18,
        hasZeroRate: true,
        primaryTaxId: { label: 'VAT Registration No.', placeholder: '123456789-7000', hint: 'SVAT/DSSL VAT Registration No.', maxLength: 14 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('LK'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    PK: {
        code: 'PK', name: 'Pakistan', flag: '🇵🇰',
        currency: 'PKR', currencySymbol: '₨',
        numberFormat: 'ur-PK', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 7,
        taxSystemName: 'GST',
        taxRates: [0, 5, 8, 18],
        defaultTaxRate: 18,
        hasZeroRate: true,
        primaryTaxId: { label: 'NTN', placeholder: '1234567-8', hint: 'National Tax Number (FBR)', maxLength: 10 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('PK'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    ID: {
        code: 'ID', name: 'Indonesia', flag: '🇮🇩',
        currency: 'IDR', currencySymbol: 'Rp',
        numberFormat: 'id-ID', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'PPN (VAT)',
        taxRates: [0, 11],
        defaultTaxRate: 11,
        hasZeroRate: true,
        primaryTaxId: { label: 'NPWP', placeholder: '01.234.567.8-901.000', hint: 'Nomor Pokok Wajib Pajak (Tax ID)', maxLength: 20 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('ID'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    TH: {
        code: 'TH', name: 'Thailand', flag: '🇹🇭',
        currency: 'THB', currencySymbol: '฿',
        numberFormat: 'th-TH', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 7],
        defaultTaxRate: 7,
        hasZeroRate: true,
        primaryTaxId: { label: 'Tax ID No.', placeholder: '0105512345678', hint: 'Thai Revenue Department Tax ID (13 digits)', maxLength: 13 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('TH'),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    },

    // ══════════════════════════════════════
    // WAVE 3 — Multi-component Tax
    // ══════════════════════════════════════

    CA: {
        code: 'CA', name: 'Canada', flag: '🇨🇦',
        currency: 'CAD', currencySymbol: 'C$',
        numberFormat: 'en-CA', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'GST/HST',
        taxRates: [0, 5, 13, 15],
        defaultTaxRate: 5,
        hasZeroRate: true,
        primaryTaxId: { label: 'Business Number (BN)', placeholder: '123456789 RT 0001', hint: 'CRA 9-digit Business Number', maxLength: 20 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: CA_ORG_TYPES,
        complianceFilings: CANADA_FILINGS,
        addressConfig: CA_ADDR,
        wave: 3,
        hasProvincialTax: true,
    },

    DE: {
        code: 'DE', name: 'Germany', flag: '🇩🇪',
        currency: 'EUR', currencySymbol: '€',
        numberFormat: 'de-DE', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'MwSt (VAT)',
        taxRates: [0, 7, 19],
        defaultTaxRate: 19,
        hasZeroRate: true,
        primaryTaxId: { label: 'USt-ID', placeholder: 'DE123456789', hint: 'Umsatzsteuer-Identifikationsnummer', maxLength: 11, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: EU_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('DE'),
        addressConfig: GENERIC_ADDR,
        wave: 3,
        isEU: true, hasReverseCharge: true, reducedRates: [7], taxLocalName: 'MwSt',
    },

    FR: {
        code: 'FR', name: 'France', flag: '🇫🇷',
        currency: 'EUR', currencySymbol: '€',
        numberFormat: 'fr-FR', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'TVA',
        taxRates: [0, 5.5, 10, 20],
        defaultTaxRate: 20,
        hasZeroRate: true,
        primaryTaxId: { label: 'Numéro TVA / SIRET', placeholder: 'FR12345678901', hint: 'French VAT number', maxLength: 13, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: EU_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('FR'),
        addressConfig: GENERIC_ADDR,
        wave: 3,
        isEU: true, hasReverseCharge: true, reducedRates: [5.5, 10], taxLocalName: 'TVA',
    },

    NL: {
        code: 'NL', name: 'Netherlands', flag: '🇳🇱',
        currency: 'EUR', currencySymbol: '€',
        numberFormat: 'nl-NL', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'BTW (VAT)',
        taxRates: [0, 9, 21],
        defaultTaxRate: 21,
        hasZeroRate: true,
        primaryTaxId: { label: 'BTW-nummer', placeholder: 'NL123456789B01', hint: 'Dutch VAT number', maxLength: 14, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: EU_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('NL'),
        addressConfig: GENERIC_ADDR,
        wave: 3,
        isEU: true, hasReverseCharge: true, reducedRates: [9], taxLocalName: 'BTW',
    },

    IE: {
        code: 'IE', name: 'Ireland', flag: '🇮🇪',
        currency: 'EUR', currencySymbol: '€',
        numberFormat: 'en-IE', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'VAT',
        taxRates: [0, 9, 13.5, 23],
        defaultTaxRate: 23,
        hasZeroRate: true,
        primaryTaxId: { label: 'VAT Registration No.', placeholder: 'IE1234567F', hint: 'Irish Revenue VAT number', maxLength: 10, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: UK_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('IE'),
        addressConfig: GENERIC_ADDR,
        wave: 3,
        isEU: true, hasReverseCharge: true, reducedRates: [9, 13.5], taxLocalName: 'VAT',
    },

    ES: {
        code: 'ES', name: 'Spain', flag: '🇪🇸',
        currency: 'EUR', currencySymbol: '€',
        numberFormat: 'es-ES', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'IVA',
        taxRates: [0, 4, 10, 21],
        defaultTaxRate: 21,
        hasZeroRate: true,
        primaryTaxId: { label: 'NIF / VAT', placeholder: 'ESA12345674', hint: 'Spanish Tax ID / VAT number', maxLength: 11, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: EU_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('ES'),
        addressConfig: GENERIC_ADDR,
        wave: 3,
        isEU: true, hasReverseCharge: true, reducedRates: [4, 10], taxLocalName: 'IVA',
    },

    IT: {
        code: 'IT', name: 'Italy', flag: '🇮🇹',
        currency: 'EUR', currencySymbol: '€',
        numberFormat: 'it-IT', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'IVA',
        taxRates: [0, 5, 10, 22],
        defaultTaxRate: 22,
        hasZeroRate: true,
        primaryTaxId: { label: 'Partita IVA', placeholder: 'IT12345678901', hint: 'Italian VAT number (11 digits)', maxLength: 13, transform: 'uppercase' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: EU_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('IT'),
        addressConfig: GENERIC_ADDR,
        wave: 3,
        isEU: true, hasReverseCharge: true, reducedRates: [5, 10], taxLocalName: 'IVA',
    },

    PT: {
        code: 'PT', name: 'Portugal', flag: '🇵🇹',
        currency: 'EUR', currencySymbol: '€',
        numberFormat: 'pt-PT', dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'IVA',
        taxRates: [0, 6, 13, 23],
        defaultTaxRate: 23,
        hasZeroRate: true,
        primaryTaxId: { label: 'NIF (Número de Identificação Fiscal)', placeholder: 'PT123456789', hint: 'Portuguese Tax ID', maxLength: 11 },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: EU_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS('PT'),
        addressConfig: GENERIC_ADDR,
        wave: 3,
        isEU: true, hasReverseCharge: true, reducedRates: [6, 13], taxLocalName: 'IVA',
    },

    // ══════════════════════════════════════
    // WAVE 4 — USA (most complex)
    // ══════════════════════════════════════

    US: {
        code: 'US', name: 'United States', flag: '🇺🇸',
        currency: 'USD', currencySymbol: '$',
        numberFormat: 'en-US', dateFormat: 'MM/DD/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'Sales Tax',
        taxRates: [0, 5, 8, 10],
        defaultTaxRate: 0,
        hasZeroRate: true,
        primaryTaxId: { label: 'EIN', placeholder: '12-3456789', hint: 'Employer Identification Number (IRS)', maxLength: 10 },
        additionalTaxIds: [
            { label: 'State Tax ID', placeholder: 'State registration number', hint: 'State sales tax permit number' },
        ],
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: US_ORG_TYPES,
        complianceFilings: USA_FILINGS,
        addressConfig: US_ADDR,
        wave: 4,
        // Phase 5 — US state-level tax
        hasStateLevelTax: true,
        hasNexusRequirement: true,
        hasProductExemptions: true,
    },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Get config for a country.
 *
 * Accepts an ISO code ('IN'), a lowercase code ('in'), or a full country name
 * ('India') — legacy orgs stored the full name, which previously fell through to
 * the generic fallback and mislabelled everything (e.g. showing "VAT"/"Tax ID"
 * for an Indian org instead of "GST"/"GSTIN"). Resolves all of these to the real
 * config; only a genuinely unknown country gets the generic fallback.
 */
export function getCountryConfig(code: string): CountryConfig {
    const raw = (code ?? '').trim();

    // 1. Exact ISO code (e.g. 'IN')
    if (COUNTRY_CONFIGS[raw]) return COUNTRY_CONFIGS[raw];

    // 2. Case-insensitive ISO code (e.g. 'in' → 'IN')
    const upper = raw.toUpperCase();
    if (COUNTRY_CONFIGS[upper]) return COUNTRY_CONFIGS[upper];

    // 3. Full country name (e.g. 'India' → IN) — legacy storage format
    const byName = Object.values(COUNTRY_CONFIGS).find(
        (c) => c.name.toLowerCase() === raw.toLowerCase(),
    );
    if (byName) return byName;

    // 4. Genuinely unknown country → generic fallback
    return {
        code: raw || code,
        name: raw || code,
        flag: '🌍',
        currency: 'USD',
        currencySymbol: '$',
        numberFormat: 'en-US',
        dateFormat: 'DD/MM/YYYY',
        fyStartMonth: 1,
        taxSystemName: 'Tax',
        taxRates: [0, 10, 20],
        defaultTaxRate: 10,
        hasZeroRate: true,
        primaryTaxId: { label: 'Tax ID', placeholder: 'TAX-ID', hint: 'Tax Registration Number' },
        hasIndianCompliance: false,
        hasDualTax: false,
        orgTypes: GENERIC_ORG_TYPES,
        complianceFilings: GENERIC_VAT_FILINGS(code),
        addressConfig: GENERIC_ADDR,
        wave: 2,
    };
}

/** Sorted list of all supported countries for dropdowns */
export const SUPPORTED_COUNTRIES = Object.values(COUNTRY_CONFIGS)
    .sort((a, b) => {
        // India first, then by wave, then alphabetically
        if (a.code === 'IN') return -1;
        if (b.code === 'IN') return 1;
        if (a.wave !== b.wave) return a.wave - b.wave;
        return a.name.localeCompare(b.name);
    });

/** All unique currencies from supported countries */
export const SUPPORTED_CURRENCIES = [
    ...new Map(
        Object.values(COUNTRY_CONFIGS).map(c => [c.currency, { code: c.currency, symbol: c.currencySymbol, name: c.name }])
    ).values()
].sort((a, b) => a.code.localeCompare(b.code));
